type Styles = Record<string, string | number>
type Keyframe = Record<string, string | number>

export interface Animation {
	addElement(el: Element | null | undefined): Animation
	addAnimation(animation: Animation | Animation[]): Animation
	keyframes(keyframes: Keyframe[]): Animation
	fromTo(property: string, from: string | number, to: string | number): Animation
	beforeStyles(styles: Styles): Animation
	beforeClearStyles(properties: string[]): Animation
	afterStyles(styles: Styles): Animation
	afterClearStyles(properties: string[]): Animation
	duration(ms: number): Animation
	delay(ms: number): Animation
	easing(easing: string): Animation
	onFinish(callback: () => void): Animation
	play(): Promise<void>
	destroy(): Animation
	/** @internal */
	parent(animation: Animation): Animation
	/** @internal */
	childFinished(): void
	/** @internal */
	timing(): { duration: number; delay: number; easing: string }
}

// The part of Ionic's animation builder this app uses, quirks kept: styles set by name as given (camelCase sets
// nothing), and a group finishes once its children and own keyframes have.
export const createAnimation = (): Animation => {
	let parentAnimation: Animation | undefined
	let durationMs: number | undefined
	let delayMs: number | undefined
	let easingValue: string | undefined
	let frames: Keyframe[] = []
	let before: Styles = {}
	let after: Styles = {}
	let initialized = false
	let running = 0
	const elements: HTMLElement[] = []
	const children: Animation[] = []
	const finishCallbacks: (() => void)[] = []
	const oneTimeCallbacks: (() => void)[] = []
	const webAnimations: globalThis.Animation[] = []

	const setStyles = (styles: Styles) =>
		elements.forEach(el => Object.entries(styles).forEach(([name, value]) => el.style.setProperty(name, String(value))))

	const finished = () => {
		setStyles(after)
		finishCallbacks.forEach(callback => callback())
		oneTimeCallbacks.splice(0).forEach(callback => callback())
		parentAnimation?.childFinished()
	}

	const ani: Animation = {
		addElement(el) {
			if (el != null) elements.push(el as HTMLElement)
			return ani
		},
		addAnimation(animation) {
			for (const child of Array.isArray(animation) ? animation : [animation]) {
				child.parent(ani)
				children.push(child)
			}
			return ani
		},
		keyframes(keyframes) {
			frames = keyframes
			return ani
		},
		fromTo(property, from, to) {
			const first = frames[0]
			if (first !== undefined && (first.offset === undefined || first.offset === 0)) first[property] = from
			else frames = [{ offset: 0, [property]: from }, ...frames]
			const last = frames[frames.length - 1]
			if (last !== undefined && (last.offset === undefined || last.offset === 1)) last[property] = to
			else frames = [...frames, { offset: 1, [property]: to }]
			return ani
		},
		beforeStyles(styles) {
			before = styles
			return ani
		},
		beforeClearStyles(properties) {
			properties.forEach(name => (before[name] = ''))
			return ani
		},
		afterStyles(styles) {
			after = styles
			return ani
		},
		afterClearStyles(properties) {
			properties.forEach(name => (after[name] = ''))
			return ani
		},
		duration(ms) {
			durationMs = ms
			return ani
		},
		delay(ms) {
			delayMs = ms
			return ani
		},
		easing(easing) {
			easingValue = easing
			return ani
		},
		onFinish(callback) {
			finishCallbacks.push(callback)
			return ani
		},
		timing() {
			const inherited = parentAnimation?.timing()
			return {
				duration: durationMs ?? inherited?.duration ?? 0,
				delay: delayMs ?? inherited?.delay ?? 0,
				easing: easingValue ?? inherited?.easing ?? 'linear',
			}
		},
		parent(animation) {
			parentAnimation = animation
			return ani
		},
		childFinished() {
			if (running === 0) return
			running--
			if (running === 0) finished()
		},
		play() {
			return new Promise(resolve => {
				if (!initialized) {
					setStyles(before)
					if (frames.length > 0) {
						const { duration, delay, easing } = ani.timing()
						for (const el of elements) {
							const animation = el.animate(frames as globalThis.Keyframe[], {
								delay,
								duration,
								easing,
								iterations: 1,
								fill: 'both',
							})
							animation.pause()
							webAnimations.push(animation)
						}
						if (webAnimations.length > 0) webAnimations[0].onfinish = () => ani.childFinished()
					}
					initialized = true
				}
				running = children.length + 1
				oneTimeCallbacks.push(() => resolve())
				children.forEach(child => child.play())
				webAnimations.forEach(animation => animation.play())
				if (frames.length === 0 || elements.length === 0) ani.childFinished()
			})
		},
		destroy() {
			children.forEach(child => child.destroy())
			webAnimations.splice(0).forEach(animation => animation.cancel())
			elements.length = 0
			children.length = 0
			frames.length = 0
			finishCallbacks.length = 0
			oneTimeCallbacks.length = 0
			initialized = false
			return ani
		},
	}

	return ani
}
